import { type Dispatch, type SetStateAction, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useAtom } from 'jotai';
import {
  ArrowLeftFromLine,
  BellIcon,
  Building2Icon,
  CirclePlus,
  Globe,
  LogOutIcon,
  MenuIcon,
  UserIcon,
} from 'lucide-react';

import { useMe } from '@/api/me';
import { useMyTenants } from '@/api/tenant';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Logo } from '@/components/ui/logo';
import { currentTenantAtom } from '@/constants/currrentTenant';
import { useUpdateLanguage } from '@/features/account/api/update-language';
import { useLogOut } from '@/features/auth/api/logout';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { cn } from '@/lib/utils';
import { getAvatarFallback } from '@/utils/avatar';

import { openModal } from '../modals/modals.utils';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { GlobalSearchInput } from './global-search-input';

type HeaderProps = {
  onClick: Dispatch<SetStateAction<boolean>>;
  isVisible: boolean;
};

type TenantType = {
  id: string;
  name: string;
  address: {
    address: string;
    city: string;
    postalCode: string;
  };
  subTenants: {
    id: string;
    name: string;
    parentTenantId: string;
  }[];
};

export const Header = ({ onClick, isVisible }: HeaderProps) => {
  const { logout } = useLogOut();
  const navigate = useNavigate();
  const location = useLocation();
  const isUnderTenantPath = /^\/tenant\/[^/]+\/.+/.test(location.pathname);

  const handleLogOut = async () => {
    await logout();
    // this seems to be the most reliable way to start the user journey from scratch
    window.location.reload();
  };

  const onClickHandler = () => onClick((prev) => !prev);

  const onAddEstateButtonClick = () => {
    openModal('create-estate', { navigate });
  };

  const updateLanguage = useUpdateLanguage();
  const updateLanguageDebounced = useDebouncedCallback(updateLanguage.mutate);

  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    updateLanguageDebounced({ language: lang });
  };

  const { data } = useMyTenants();

  const [currentTenant, setCurrentTenant] = useAtom(currentTenantAtom);
  const me = useMe({
    tenantId: currentTenant?.id,
  });

  const updateCurrentTenant = (tenant: TenantType | TenantType['subTenants'][number]) => {
    setCurrentTenant({
      id: tenant.id,
      name: tenant.name,
    });
    me.refetch();
    toast.success('Updated the current tenant');
  };

  useEffect(() => {
    if (data) {
      setCurrentTenant(data[0]);
    }
  }, [data, setCurrentTenant]);

  return (
    <header className="flex h-[60px] items-center justify-between rounded-sm border-b border-gray-200 bg-primary-lighter px-4 lg:pl-10 lg:pr-14">
      <Button className="border-0 lg:hidden" variant="outline" size="sm" onClick={onClickHandler}>
        {isVisible ? <ArrowLeftFromLine /> : <MenuIcon />}
      </Button>
      <Logo className="!h-8 sm:block sm:h-auto" />
      <div className="ml-2 hidden w-full max-w-screen-sm items-center gap-2 sm:flex">
        <GlobalSearchInput />
        <CirclePlus
          onClick={onAddEstateButtonClick}
          className="mx-auto size-12 cursor-pointer text-primary-light sm:mx-0"
        />
      </div>
      <div className="items-center gap-[21px] sm:flex">
        <BellIcon className="hidden sm:block" size={28} />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Avatar className="size-10 cursor-pointer sm:size-12">
              <AvatarImage
                src={`/images/${me.data.images?.mediaId}?size=thumbnail100`}
                alt="avatar"
              />
              <AvatarFallback>{getAvatarFallback(me.data)}</AvatarFallback>
            </Avatar>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuItem asChild>
              <Link to="/profile">
                <UserIcon />
                Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="group">
                <div className="flex items-center gap-4">
                  <Building2Icon className="flex-shrink-0" />
                  <div className="flex flex-col leading-5">
                    {t('tenant')}
                    {currentTenant ? (
                      <span className="text-xs text-neutral-500 group-hover:text-white">
                        {currentTenant.name}
                      </span>
                    ) : null}
                  </div>
                </div>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {data.map((tenant: TenantType, index: number) => {
                  return (
                    <div key={tenant.id}>
                      <DropdownMenuItem
                        className={cn(
                          'cursor-pointer px-6 py-3 leading-tight',
                          currentTenant?.id === tenant.id ? 'bg-primary text-white' : 'bg-gray-100'
                        )}
                        onClick={() => {
                          if (isUnderTenantPath) {
                            navigate({
                              to: '/tenant/$tenantId/users',
                              params: { tenantId: tenant.id },
                            });
                          }
                          updateCurrentTenant(tenant);
                        }}
                      >
                        <>{tenant.name}</>
                      </DropdownMenuItem>
                      {tenant.subTenants?.map((subTenant: TenantType['subTenants'][number]) => {
                        return (
                          <DropdownMenuItem
                            key={subTenant.name}
                            className={cn(
                              'my-2 ml-4 cursor-pointer',
                              currentTenant?.id === subTenant.id
                                ? 'bg-primary text-white'
                                : 'bg-gray-100 hover:bg-primary hover:text-white'
                            )}
                            onClick={() => {
                              if (isUnderTenantPath) {
                                navigate({
                                  to: '/tenant/$tenantId/users',
                                  params: { tenantId: subTenant.id },
                                });
                              }
                              updateCurrentTenant(subTenant);
                            }}
                          >
                            {subTenant.name}
                          </DropdownMenuItem>
                        );
                      })}
                      {index !== data.length - 1 ? <DropdownMenuSeparator /> : null}
                    </div>
                  );
                })}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <div className="flex items-center gap-4">
                  <Globe />
                  <div className="flex flex-col leading-5">
                    {t('language')}
                    <span className="uppercase">{currentLang}</span>
                  </div>
                </div>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem
                  className={cn(
                    currentLang === 'en'
                      ? 'pointer-events-none cursor-not-allowed text-neutral-400'
                      : 'cursor-pointer'
                  )}
                  disabled={currentLang === 'en'}
                  onClick={() => currentLang !== 'en' && changeLanguage('en')}
                >
                  {t('english')}
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                  className={cn(
                    currentLang === 'de'
                      ? 'pointer-events-none cursor-not-allowed text-neutral-400'
                      : 'cursor-pointer'
                  )}
                  disabled={currentLang === 'de'}
                  onClick={() => currentLang !== 'de' && changeLanguage('de')}
                >
                  {t('german')}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link onClick={handleLogOut} preload={false}>
                <LogOutIcon />
                Logout
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
